// Copyright (c) StrangeBee 2023

import { ICONS } from '@Constants/iconsApp';
import { copyToClipboard } from '@Helpers/copy';
import { useLocalMessage } from '@Helpers/locale';
import { openMessage } from '@Helpers/notification';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { Space } from 'antd';
import React, { useMemo, useState } from 'react';

import './FunctionTerminal.scss';

type TerminalMode = 'input' | 'stdout' | 'stderr' | 'result';

interface FunctionTerminalProps {
    hasInput?: boolean;
    duration?: number;
    result?: string;
    stdout?: string;
    stderr?: string;
    modeValue?: TerminalMode;
    onModeChange?: (mode: TerminalMode) => void;
    defaultMode?: TerminalMode;
    onDryRunClick?: () => void;
    onRunClick?: () => void;
}

export const FunctionTerminal = ({
    hasInput,
    duration,
    result,
    stdout,
    stderr,
    modeValue,
    onModeChange,
    defaultMode,
    onDryRunClick,
    onRunClick,
}: FunctionTerminalProps) => {
    const i = useLocalMessage();

    const [mode, setMode] = useState<TerminalMode>(defaultMode || 'stdout');

    const modeParsed = useMemo(() => modeValue || mode, [modeValue, mode]);
    const resultAsString = typeof result === 'object' ? JSON.stringify(result) : result?.toString();

    return (
        <div className="function-terminal">
            <div className="function-terminal__header">
                <div className="function-terminal__header__left">
                    <THRadio.Group
                        buttonStyle="solid"
                        value={modeParsed}
                        onChange={(e) => onModeChange?.(e.target.value) || setMode(e.target.value)}
                    >
                        {hasInput && <THRadio.Button value="input">input</THRadio.Button>}
                        {hasInput && <THRadio.Button value="result">result</THRadio.Button>}
                        <THRadio.Button value="stdout">stdout</THRadio.Button>
                        <THRadio.Button value="stderr">stderr</THRadio.Button>
                    </THRadio.Group>

                    {duration !== undefined && (
                        <div className="function-form__execution">{`${i(
                            'function.manage.output.execution',
                        )}: ${duration}s`}</div>
                    )}
                </div>
                <div className="function-terminal__header__right">
                    {(stdout || stderr) && modeParsed !== 'input' && (
                        <THButton
                            type="link"
                            hidden={!(modeParsed == 'stdout' || modeParsed == 'stderr')}
                            onClick={() => {
                                copyToClipboard(modeParsed === 'stdout' ? stdout || '' : stderr || '');
                                openMessage('success', i('notification.copy'));
                            }}
                        >
                            {modeParsed === 'stdout'
                                ? i('function.manage.output.stdout')
                                : i('function.manage.output.stderr')}
                            <ICONS.CopyTwoTone />
                        </THButton>
                    )}

                    <Space>
                        {onDryRunClick && (
                            <THButton type="default" onClick={onDryRunClick}>
                                {i('function.manage.test.dry-run')}
                            </THButton>
                        )}

                        {onRunClick && (
                            <THButton type="primary" onClick={onRunClick}>
                                {i('function.manage.test.run')}
                            </THButton>
                        )}
                    </Space>
                </div>
            </div>

            <div className="function-terminal__output">
                {hasInput && (
                    <THTextArea.Helper
                        hidden={modeParsed !== 'input'}
                        name="input"
                        placeholder={`{\n\t"key": "value"\n}`}
                        valueFormat="json"
                        hideHelper
                    />
                )}

                {modeParsed !== 'input' && (
                    <pre className="function-terminal__output__std">
                        <code>
                            {modeParsed === 'stdout' ? stdout : modeParsed === 'result' ? resultAsString : stderr}
                        </code>
                    </pre>
                )}
            </div>
        </div>
    );
};
